import {
  AppBar,
  Avatar,
  Box,
  Button,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Toolbar,
  Typography,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import useClientContext from "../../../../context/useClientContext";
import useUserContext from "../../../../context/useUserContext";
import type { PortalStyleSettings } from "./investorPortalStyleState";
import { logoTypeHeights } from "./investorPortalStyleState";
import usePreviewTheme from "./usePreviewTheme";

const skeletonWidths = ["70%", "45%", "60%", "50%", "80%", "55%"];
const skeletonRowCount = 7;

interface Props {
  settings: PortalStyleSettings;
}

const PortalPagePreview = ({ settings }: Props) => {
  const { title: clientTitle } = useClientContext();
  const { name: userName } = useUserContext();

  const previewTheme = usePreviewTheme(settings.mainColor);

  return (
    <ThemeProvider theme={previewTheme}>
      <Paper inert sx={{ overflow: "hidden" }}>
        <AppBar position="static" elevation={0} sx={{ bgcolor: settings.appBarColor }}>
          <Toolbar sx={{ gap: 4 }}>
            {settings.logoTypeUrl ? (
              <Box
                component="img"
                src={settings.logoTypeUrl}
                alt=""
                sx={{ height: logoTypeHeights[settings.logoTypeSize], minWidth: 0, objectFit: "contain" }}
              />
            ) : (
              <Typography variant="h6" sx={{ color: settings.appBarTextColor }}>
                Logotype
              </Typography>
            )}
            <Stack direction="row" spacing={3} sx={{ flexGrow: 1, color: settings.appBarTextColor }}>
              <Typography sx={{ color: "primary.main" }}>Home</Typography>
              <Typography>Page 1</Typography>
              <Typography>Page 2</Typography>
            </Stack>
            <Avatar />
          </Toolbar>
        </AppBar>

        <Stack
          sx={{
            px: 5,
            py: 3,
            bgcolor: "subtleSurfaceBg",
            backgroundImage: settings.bannerUrl ? `url(${settings.bannerUrl})` : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <Typography variant="subtitle1" sx={{ color: settings.bannerFontColor }}>
            {userName}
          </Typography>
          <Typography sx={{ color: settings.bannerFontColor }}>
            {`Welcome to the ${clientTitle} Investor Portal`}
          </Typography>
        </Stack>

        <Stack spacing={3} sx={{ p: 3 }}>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableBody>
                {Array.from({ length: skeletonRowCount }, (_, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {skeletonWidths.map((_, columnIndex) => (
                      // eslint-disable-next-line @eslint-react/no-array-index-key
                      <TableCell key={columnIndex} sx={{ p: 2 }}>
                        <Skeleton
                          variant="rounded"
                          animation={false}
                          height={8}
                          width={skeletonWidths[(rowIndex + columnIndex) % skeletonWidths.length]}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack direction="row" spacing={1}>
            <Button variant="contained">Download</Button>
            <Button>View</Button>
          </Stack>
        </Stack>
      </Paper>
    </ThemeProvider>
  );
};

export default PortalPagePreview;
